import { useState } from 'react'
import type { AttachedFile } from '@/lib/api'
import { formatElapsed, makeTranscriptFile, type NoteCustomer } from '@/lib/voiceNotes'
import { useVoiceRecorder } from '@/hooks/useVoiceRecorder'
import { useBranding } from '@/contexts/BrandingContext'
import { Spinner } from '@/components/Spinner'
import { NoteStylePicker } from './NoteStylePicker'
import { CustomerPicker } from './CustomerPicker'
import { TranscriptModal } from './TranscriptModal'

// Operator-only row above the composer: "Start taking notes" records the
// conversation (Android/iOS PWA and desktop), transcribes it server-side in
// ~5-minute parts, and hands the finished transcript to an editable preview
// that attaches it to the next message. The note style picker chooses which
// saved summarisation instructions ride with whatever is sent; the customer
// picker pins WHO the notes are about — both ride with every send.

interface VoiceNotesBarProps {
  disabled?: boolean
  noteStyleId: string | null
  onNoteStyleChange: (id: string | null) => void
  noteCustomer: NoteCustomer | null
  onNoteCustomerChange: (customer: NoteCustomer | null) => void
  onAttachTranscript: (file: AttachedFile) => void
}

export function VoiceNotesBar({ disabled, noteStyleId, onNoteStyleChange, noteCustomer, onNoteCustomerChange, onAttachTranscript }: VoiceNotesBarProps) {
  const branding = useBranding()
  const [finished, setFinished] = useState<{ text: string; durationSec: number } | null>(null)
  const [barError, setBarError] = useState<string | null>(null)

  const recorder = useVoiceRecorder({
    onFinished: (transcript, durationSec) => {
      if (!transcript.trim()) setBarError('No speech was detected in that recording.')
      else setFinished({ text: transcript, durationSec })
    },
  })

  const handleStart = () => {
    setBarError(null)
    void recorder.start()
  }

  const handleCancel = () => {
    if (window.confirm('Discard this recording? Nothing will be transcribed.')) recorder.cancel()
  }

  const error = barError ?? recorder.error

  return (
    <div className="flex flex-col bg-card px-3 pt-2">
      {recorder.state === 'idle' && (
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            className="rounded-2xl border border-blue-200 bg-blue-50 px-3 py-1.5 text-[13px] font-medium text-blue-700 disabled:opacity-40"
            onClick={handleStart}
            disabled={disabled || !recorder.isSupported}
            title={recorder.isSupported ? undefined : 'Voice recording is not supported in this browser'}
          >
            🎙 Start taking notes
          </button>
          <NoteStylePicker selectedId={noteStyleId} onSelect={onNoteStyleChange} disabled={disabled} />
          <CustomerPicker selected={noteCustomer} onSelect={onNoteCustomerChange} disabled={disabled} />
        </div>
      )}

      {recorder.state === 'recording' && (
        <div className="flex flex-col gap-0.5">
          <div className="flex flex-wrap items-center gap-2 rounded-2xl border border-red-200 bg-red-50 px-3 py-1.5">
            <span className="h-2.5 w-2.5 animate-pulse rounded-full bg-red-500" aria-hidden />
            <span className="text-[13px] font-semibold text-red-700">
              Recording {formatElapsed(recorder.elapsedSec)}
            </span>
            {recorder.transcribingCount > 0 && (
              <span className="text-[12px] text-red-700/70">
                transcribing {recorder.transcribingCount} part{recorder.transcribingCount > 1 ? 's' : ''}…
              </span>
            )}
            <span className="flex-1" />
            <button
              type="button"
              className="rounded-xl px-3 py-1 text-[13px] font-semibold text-white"
              style={{ backgroundColor: branding.primaryColor }}
              onClick={recorder.stop}
            >
              Stop
            </button>
            <button
              type="button"
              className="px-1.5 text-[14px] text-muted-foreground"
              onClick={handleCancel}
              aria-label="Discard recording"
            >
              ✕
            </button>
          </div>
          <p className="px-1 text-[11px] text-muted-foreground/70">
            Keep the app open while recording — it stops if the screen locks.
          </p>
        </div>
      )}

      {recorder.state === 'finishing' && (
        <div className="flex items-center gap-2 px-1 py-1.5" style={{ color: branding.primaryColor }}>
          <Spinner size={16} className="text-current" />
          <span className="text-[13px] italic text-muted-foreground">Finishing transcription…</span>
        </div>
      )}

      {error && (
        <div className="mt-1 flex items-start justify-between gap-2 px-1">
          <p className="text-[12px] text-rose-700">{error}</p>
          <button
            type="button"
            className="shrink-0 text-[12px] text-muted-foreground"
            onClick={() => { setBarError(null); recorder.clearError() }}
            aria-label="Dismiss error"
          >
            ✕
          </button>
        </div>
      )}

      {finished && (
        <TranscriptModal
          transcript={finished.text}
          durationSec={finished.durationSec}
          onAttach={(finalText) => {
            onAttachTranscript(makeTranscriptFile(finalText, new Date(), finished.durationSec))
            setFinished(null)
          }}
          onDiscard={() => setFinished(null)}
        />
      )}
    </div>
  )
}
